import { Col, message, PageHeader, Row, Select, Space, Spin, Tabs, Tag } from "antd";
import axios from "axios";
import React, { Component } from "react";
import { connect } from "react-redux";
import {
  SelectItem,
  Form,
  FormItem,
  TableItem,
  dineroDisabledMask,
  ButtonItem,
  ErrorItem,
  InputItem,
} from "../../../../Components/Items";
import { FileExcelOutlined, FilePdfOutlined } from "@ant-design/icons";
import moment from "moment";
import "moment/locale/es";
import {
  descargarExcel,
  descargarPdf,
} from "./exportarInsumosYProveedores";

moment.locale("es");

const { Option } = Select;
const { TabPane } = Tabs;

const COLOR_NEUTRO = "#595959";

const sorterTexto = (campo) => (a, b) =>
  (a[campo] || "").toString().localeCompare((b[campo] || "").toString());

const sorterNumero = (campo) => (a, b) => (a[campo] || 0) - (b[campo] || 0);

/**
 * Reporte Insumos Y Proveedores.
 *
 * Un renglon por cada entrada de `Insumo.relaciones[]` — el registro que liga
 * un insumo con un proveedor, con el codigo que ese proveedor le da al
 * producto, el costo pactado en SU moneda, el factor de conversion a la unidad
 * de inventario y la compra minima — mas un renglon por cada insumo que
 * todavia no tiene ningun proveedor relacionado, marcado en rojo.
 *
 * Es el catalogo, no el historico de compras: el costo que se muestra es el
 * pactado, no el de la ultima compra real (eso lo da Costo Vs Ultima Compra).
 */
class ReporteInsumosYProveedores extends Component {
  state = {
    loading: false,
    datos: [],
    resumen: {},
    consultado: false,
    filtros: {},
    objetoProveedores: null,
    objetoFamilias: null,
  };

  atras() {
    this.props.history.goBack();
  }

  async componentDidMount() {
    this.setState({ loading: true });
    // Cada catalogo lleva su propio `.catch()`: sin el, uno que falle rechaza
    // el Promise.all, corta el componentDidMount y la consulta inicial nunca se
    // lanza — la pantalla se queda con el spinner puesto y sin ningun mensaje.
    // Un filtro sin catalogo es una degradacion aceptable; una pantalla colgada
    // no lo es.
    await Promise.all([
      axios
        .post(
          "proveedores/selectProveedores",
          { empresaId: this.props.empresaId },
          { headers: this.props.headersToken }
        )
        .then((res) => {
          if (res.data.status !== 404) {
            this.setState({
              objetoProveedores: res.data.map((value) => (
                <Option value={value._id} key={value._id}>
                  {value.stringBuscar}
                </Option>
              )),
            });
          }
        })
        .catch(() => {
          message.error("No se pudo cargar el catalogo de proveedores");
        }),
      axios
        .post(
          "familias/selectFamilias",
          { empresaId: this.props.empresaId },
          { headers: this.props.headersToken }
        )
        .then((res) => {
          if (res.data.status !== 404) {
            this.setState({
              objetoFamilias: res.data.map((value) => (
                <Option value={value._id} key={value._id}>
                  {value.nombre}
                </Option>
              )),
            });
          }
        })
        .catch(() => {
          message.error("No se pudo cargar el catalogo de familias");
        }),
    ]);
    this.consultarReporte();
  }

  consultarReporte(values = {}) {
    this.setState({ loading: true });
    axios
      .post(
        "reportesDeCompras/reporteInsumosProveedores",
        { ...values, empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({
            loading: false,
            consultado: true,
            datos: res.data.datos || [],
            resumen: res.data.resumen || {},
            filtros: values,
          });
        } else if (res.data.message) {
          // success 1: el filtro devuelve mas renglones que el tope.
          message.warning(res.data.message);
          this.setState({ loading: false });
        } else {
          message.error("Ha sucedido un error y no se cargo bien el reporte");
          this.setState({ loading: false });
        }
      })
      .catch(() => {
        message.error("Error de conexion");
        this.setState({ loading: false });
      });
  }

  descripcionFiltros() {
    const filtros = this.state.filtros || {};
    const partes = [];
    if ((filtros.proveedores || []).length) {
      partes.push(`${filtros.proveedores.length} proveedor(es)`);
    } else {
      partes.push("Todos los proveedores");
    }
    if ((filtros.familias || []).length) {
      partes.push(`${filtros.familias.length} familia(s)`);
    }
    if (filtros.insumo) {
      partes.push(`Insumo: "${filtros.insumo}"`);
    }
    return partes.join(" - ");
  }

  parametrosExportacion() {
    return {
      datos: this.state.datos,
      resumen: this.state.resumen,
      empresaNombre: this.props.empresa,
      descripcionFiltros: this.descripcionFiltros(),
    };
  }

  onFinishFailed(e) {
    ErrorItem(e);
  }

  render() {
    const columns = [
      {
        title: "Clave",
        dataIndex: "claveInsumo",
        key: "claveInsumo",
        width: "7%",
        // `buscar` compara contra el valor CRUDO del registro, no contra lo que
        // pinta el render.
        buscar: ["claveInsumo", "codigoInsumo"],
        sorter: sorterTexto("claveInsumo"),
      },
      {
        title: "Descripcion",
        dataIndex: "descripcionInsumo",
        key: "descripcionInsumo",
        width: "15%",
        buscar: "descripcionInsumo",
        sorter: sorterTexto("descripcionInsumo"),
      },
      {
        title: "Familia",
        dataIndex: "nombreFamilia",
        key: "nombreFamilia",
        width: "8%",
        sorter: sorterTexto("nombreFamilia"),
      },
      {
        title: "Proveedor",
        dataIndex: "proveedor",
        key: "proveedor",
        width: "13%",
        buscar: "proveedor",
        sorter: sorterTexto("proveedor"),
        render: (valor, record) =>
          record.sinProveedor ? (
            <Tag color="red">Sin proveedor</Tag>
          ) : (
            <span>
              {valor}
              {record.esProveedorDefault && (
                <Tag color="cyan" style={{ marginLeft: 5 }}>
                  Default
                </Tag>
              )}
              {record.esPrincipal && !record.esProveedorDefault && (
                <Tag color="blue" style={{ marginLeft: 5 }}>
                  Principal
                </Tag>
              )}
            </span>
          ),
      },
      {
        title: "Cod. Prov.",
        dataIndex: "codigoProveedor",
        key: "codigoProveedor",
        width: "7%",
        buscar: "codigoProveedor",
        sorter: sorterTexto("codigoProveedor"),
      },
      {
        title: "Costo",
        dataIndex: "costo",
        key: "costo",
        width: "8%",
        align: "right",
        sorter: sorterNumero("costo"),
        render: (valor, record) =>
          valor === null || valor === undefined ? (
            ""
          ) : (
            <span>
              {dineroDisabledMask(valor)}
              {record.abreviacionMoneda ? ` ${record.abreviacionMoneda}` : ""}
            </span>
          ),
      },
      // Las dos unidades van flanqueando la conversion, en el orden en que se
      // lee el renglon: el costo esta en la unidad del PROVEEDOR, se divide
      // entre el factor y el costo unitario queda en la de INVENTARIO. El
      // backend ya manda el texto "CLAVE - Nombre" armado, por eso no hay
      // `render`: asi `buscar` y `sorter` trabajan sobre el mismo valor que se
      // ve en pantalla.
      {
        title: "U. Prov.",
        dataIndex: "unidadProveedor",
        key: "unidadProveedor",
        width: "8%",
        buscar: "unidadProveedor",
        sorter: sorterTexto("unidadProveedor"),
      },
      {
        title: "Conv.",
        dataIndex: "conversion",
        key: "conversion",
        width: "5%",
        align: "right",
        sorter: sorterNumero("conversion"),
        render: (valor) => (valor ? `x${valor}` : ""),
      },
      {
        title: "Costo Unit.",
        dataIndex: "costoUnitario",
        key: "costoUnitario",
        width: "8%",
        align: "right",
        sorter: sorterNumero("costoUnitario"),
        render: (valor) =>
          valor === null || valor === undefined ? "" : dineroDisabledMask(valor),
      },
      {
        title: "U. Inv.",
        dataIndex: "unidadInventario",
        key: "unidadInventario",
        width: "8%",
        buscar: "unidadInventario",
        sorter: sorterTexto("unidadInventario"),
      },
      {
        title: "Compra Min.",
        dataIndex: "compraMin",
        key: "compraMin",
        width: "5%",
        align: "right",
        sorter: sorterNumero("compraMin"),
        render: (valor) => (valor === null || valor === undefined ? "" : valor),
      },
      {
        title: "Ultimo Cambio",
        dataIndex: "fechaUltimoCambioCosto",
        key: "fechaUltimoCambioCosto",
        width: "8%",
        align: "center",
        sorter: (a, b) =>
          moment(a.fechaUltimoCambioCosto || 0).valueOf() -
          moment(b.fechaUltimoCambioCosto || 0).valueOf(),
        render: (valor) => (valor ? moment(valor).format("DD/MM/YYYY") : ""),
      },
    ];

    const resumen = this.state.resumen || {};

    return (
      <Form
        name="reporteInsumosYProveedores"
        onFinish={this.consultarReporte.bind(this)}
        onFinishFailed={this.onFinishFailed}
      >
        <PageHeader
          onBack={this.atras.bind(this)}
          title={
            "Reporte Insumos Y Proveedores" +
            (this.state.consultado
              ? `: ${resumen.relaciones || 0} relaciones en ${resumen.insumos || 0} insumos`
              : "")
          }
          style={{
            border: "1px solid rgb(235, 237, 240)",
            backgroundColor: "white",
            marginBottom: 10,
          }}
          extra={
            <div style={{ display: "flex", justifyContent: "right" }}>
              <Space>
                {this.state.datos.length > 0 && (
                  <ButtonItem
                    blanco
                    onClick={() => descargarExcel(this.parametrosExportacion())}
                    icon={<FileExcelOutlined />}
                    style={{ display: "block", margin: "0 0 0 auto" }}
                  >
                    Descargar Excel
                  </ButtonItem>
                )}
                {this.state.datos.length > 0 && (
                  <ButtonItem
                    blanco
                    onClick={() => descargarPdf(this.parametrosExportacion())}
                    icon={<FilePdfOutlined />}
                    style={{ display: "block", margin: "0 0 0 auto" }}
                  >
                    Descargar PDF
                  </ButtonItem>
                )}
                <ButtonItem
                  type="primary"
                  htmlType="submit"
                  style={{ display: "block", margin: "0 0 0 auto" }}
                >
                  Consultar
                </ButtonItem>
              </Space>
            </div>
          }
        />
        <Spin spinning={this.state.loading}>
          <div
            style={{
              backgroundColor: "white",
              padding: 20,
              paddingTop: 5,
              borderRadius: 5,
              boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
            }}
          >
            <Tabs
              style={{ margin: 0 }}
              tabBarExtraContent={{
                left: <h1 style={{ padding: 0, margin: "0 10px 0 0" }}>Filtros:</h1>,
              }}
            >
              {/* forceRender: sin el, los campos de una pestaña que el usuario
                  nunca abre no llegan a registrarse en el Form y se pierden
                  del submit. */}
              <TabPane tabKey="1" key="1" forceRender tab="Proveedor y familia">
                <Row>
                  <Col span={12}>
                    <FormItem
                      label="Proveedores"
                      name="proveedores"
                      margin
                      tooltip="Deja este campo vacio para ver el catalogo completo. Al filtrar por proveedor, los insumos que no tienen ningun proveedor relacionado dejan de aparecer."
                    >
                      <SelectItem
                        width="95%"
                        placeholder="Todos los proveedores"
                        mode="multiple"
                      >
                        {this.state.objetoProveedores}
                      </SelectItem>
                    </FormItem>
                  </Col>
                  <Col span={12}>
                    <FormItem
                      label="Familias"
                      name="familias"
                      margin
                      tooltip="Acota el catalogo a una o varias familias de insumos."
                    >
                      <SelectItem
                        width="95%"
                        placeholder="Todas las familias"
                        mode="multiple"
                      >
                        {this.state.objetoFamilias}
                      </SelectItem>
                    </FormItem>
                  </Col>
                </Row>
              </TabPane>
              <TabPane tabKey="2" key="2" forceRender tab="Insumo">
                <Row>
                  <Col span={12}>
                    <FormItem
                      label="Buscar insumo"
                      name="insumo"
                      margin
                      tooltip="Busca dentro del codigo, la clave, la descripcion o el codigo de barras del insumo. Deja el campo vacio para no acotar."
                    >
                      <InputItem placeholder="Codigo, clave o descripcion" />
                    </FormItem>
                  </Col>
                </Row>
              </TabPane>
            </Tabs>
          </div>
          <div style={{ height: 10 }} />
          <div style={{ backgroundColor: "white", padding: 20, borderRadius: 5 }}>
            {this.state.consultado && (
              <p style={{ color: COLOR_NEUTRO, marginBottom: 10 }}>
                {`Proveedores distintos: ${resumen.proveedores || 0} - `}
                {`Insumos sin proveedor: ${resumen.sinProveedor || 0} - `}
                {`Relaciones sin costo: ${resumen.sinCosto || 0}`}
                {" - El costo mostrado es el pactado en el catalogo, no el de la ultima compra real."}
              </p>
            )}
            <TableItem
              size="small"
              dataSource={this.state.datos}
              columns={columns}
              rowClassName={(record) => (record.sinProveedor ? "revisar" : "")}
              rowKey={(value) =>
                `${value.insumoId}-${value.relacionId || "sin"}`
              }
            />
          </div>
        </Spin>
      </Form>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
  empresa: state.empresa.nombre,
});

export default connect(mapStateToProps)(ReporteInsumosYProveedores);
